import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { Cover } from '../../components/primitives/Cover';
import { formatDuration } from '../../utils/trackFormatting';
import type { BrowseTrackItem } from '../hooks/useBrowseSearch';
import { formatChange, formatCount, formatReach } from '../browseFormat';
import { browseMessages as messages } from '../browseMessages';
import { BrowseCostChip } from './BrowseCostChip';
import { BrowseFollowButton } from './BrowseFollowButton';
import { BrowseOpenLink } from './BrowseOpenLink';
import { changeToneClass } from './changeToneClass';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './BrowseRows.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface BrowseTrackRowProps {
  track: BrowseTrackItem;
  rank: number;
  /** The short row under a top artist (design a1): cover, name, reach, Follow. */
  compact?: boolean;
}

/** One track row (design a1 Tracks panel, a2; c1 on a phone). */
const BrowseTrackRowComponent: React.FC<BrowseTrackRowProps> = ({ track, rank, compact = false }) => {
  const intl = useIntl();
  const followed = track.state === 'followed';
  const duration = formatDuration(track.durationMs);
  const sub = [track.artistName, track.albumName, duration].filter((part) => part !== null && part !== '').join(' · ');
  const follow = (iconOnly: boolean) => (
    <BrowseFollowButton kind="track" id={track.id} name={track.name} followed={followed} iconOnly={iconOnly} />
  );

  if (compact) {
    return (
      <div className={clsx(styles.resultRow, styles.compactGrid)}>
        <Cover src={track.imageUrl} label={track.name} size={40} />
        <BrowseOpenLink kind="track" id={track.id} state={track.state} className={styles.nameCell}>
          <span className={styles.nameMain}>{track.name}</span>
          <span className={styles.nameSub}>
            {intl.formatMessage(messages.playlistsShort, { count: track.playlists ?? 0 })}
            {' · '}
            {intl.formatMessage(messages.reachShort, { value: formatReach(intl, track.reach) })}
          </span>
        </BrowseOpenLink>
        <span className={clsx(styles.num, changeToneClass(track.change7d))}>{formatChange(intl, track.change7d)}</span>
        <span className={styles.right}>{follow(false)}</span>
      </div>
    );
  }

  return (
    <div className={clsx(styles.resultRow, styles.trackGrid)} data-testid="browse-track-row">
      <span className={clsx(styles.rank, styles.hideOnPhone)}>{rank}</span>
      <Cover src={track.imageUrl} label={track.name} size={44} />
      <BrowseOpenLink kind="track" id={track.id} state={track.state} className={styles.nameCell}>
        <span className={styles.nameMain}>{track.name}</span>
        <span className={styles.nameSub}>{sub}</span>
        <span className={clsx(styles.nameSub, styles.phoneFigure, styles.showOnPhone)}>
          {intl.formatMessage(messages.reachShort, { value: formatReach(intl, track.reach) })}
        </span>
        <BrowseCostChip state={track.state} className={styles.showOnPhone} />
      </BrowseOpenLink>
      <span className={clsx(styles.num, styles.hideOnPhone)}>{formatCount(intl, track.playlists)}</span>
      <span className={clsx(styles.num, styles.hideOnPhone)}>{formatReach(intl, track.reach)}</span>
      <span className={clsx(styles.num, styles.hideOnPhone, changeToneClass(track.change7d))}>
        {formatChange(intl, track.change7d)}
      </span>
      <BrowseCostChip state={track.state} className={styles.hideOnPhone} />
      <span className={clsx(styles.hideOnPhone, styles.right)}>{follow(false)}</span>
      <span className={clsx(styles.showOnPhone, styles.right)}>{follow(true)}</span>
    </div>
  );
};

export const BrowseTrackRow = TypedMemo(BrowseTrackRowComponent);
